import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="informe-plantillas"
export default class extends Controller {
  static targets = ["select", "modalSelect", "templateModal", "nameInput", "contentInput", "templateEditor", "trixEditor", "formTitle", "saveButton"]

  static values = {
    indexUrl: String,
    createUrl: String,
    updateUrlTemplate: String,
    readonly: Boolean
  }

  connect() {
    this.templates = []
    this.editingTemplateId = null
    this.templateModal = null
    this.loadTemplatesFromServer()
  }

  toggleNewTemplateForm() {
    if (this.readonlyValue || !this.hasTemplateModalTarget) return

    this.prepareCreateForm()
    this.openTemplateModal()
  }

  onModalSelectChange() {
    if (!this.hasModalSelectTarget) return

    const templateId = this.modalSelectTarget.value
    if (!templateId) {
      this.prepareCreateForm()
      return
    }

    const template = this.templates.find((t) => String(t.id) === String(templateId))
    if (!template) return

    this.prepareEditForm(template)
  }

  cancelNewTemplate() {
    this.closeTemplateModal()
  }

  async saveTemplate() {
    if (this.readonlyValue) return

    const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
    const content = this.templateContent().trim()

    if (!name || !content) {
      window.alert("Debe completar el nombre y el contenido de la plantilla.")
      return
    }

    try {
      if (this.editingTemplateId) {
        const response = await this.updateTemplate(this.editingTemplateId, { nombre: name, contenido: content })
        this.selectedTemplateId = response.plantilla?.id
      } else {
        const response = await this.createTemplate({ nombre: name, contenido: content })
        this.selectedTemplateId = response.plantilla?.id
      }

      await this.loadTemplatesFromServer()
      this.resetTemplateForm()
      this.closeTemplateModal()
    } catch (error) {
      window.alert(error.message || "No se pudo guardar la plantilla.")
    }
  }

  appendTemplate() {
    if (!this.hasSelectTarget || !this.hasTrixEditorTarget) return

    const templateId = this.selectTarget.value
    if (!templateId) return

    const selectedTemplate = this.templates.find((template) => String(template.id) === String(templateId))
    if (!selectedTemplate) return

    const existingHtml = this.reportContent().trim()
    const templateHtml = (selectedTemplate.contenido || "").trim()
    if (!templateHtml) return

    const mergedHtml = existingHtml.length > 0
      ? `${existingHtml}<div><br></div>${templateHtml}`
      : templateHtml

    this.loadReportHtml(mergedHtml)
    this.trixEditorTarget.focus()

    // Allow selecting the same template again later if needed.
    this.selectTarget.value = ""
  }

  renderTemplateOptions() {
    if (!this.hasSelectTarget) return

    const baseOption = '<option value="">Seleccione una plantilla</option>'
    const options = this.templates
      .map((template) => `<option value="${template.id}">${this.escapeHtml(template.nombre)}</option>`)
      .join("")

    this.selectTarget.innerHTML = `${baseOption}${options}`

    if (this.selectedTemplateId) {
      this.selectTarget.value = String(this.selectedTemplateId)
    }

    if (this.hasModalSelectTarget) {
      const modalBaseOption = '<option value="">— Nueva plantilla —</option>'
      this.modalSelectTarget.innerHTML = `${modalBaseOption}${options}`
    }
  }

  async loadTemplatesFromServer() {
    if (!this.hasIndexUrlValue || !this.indexUrlValue) return

    try {
      const response = await fetch(this.indexUrlValue, {
        headers: {
          Accept: "application/json"
        }
      })

      if (!response.ok) throw new Error("No se pudieron cargar las plantillas.")

      const data = await response.json()
      this.templates = Array.isArray(data.plantillas) ? data.plantillas : []
      this.renderTemplateOptions()
    } catch (error) {
      this.templates = []
      this.renderTemplateOptions()
      window.alert(error.message || "No se pudieron cargar las plantillas.")
    }
  }

  async createTemplate(payload) {
    if (!this.hasCreateUrlValue || !this.createUrlValue) {
      throw new Error("No está configurada la URL para crear plantillas.")
    }

    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ plantilla_informe: payload })
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || "No se pudo crear la plantilla.")
    }

    return data
  }

  async updateTemplate(id, payload) {
    if (!this.hasUpdateUrlTemplateValue || !this.updateUrlTemplateValue) {
      throw new Error("No está configurada la URL para editar plantillas.")
    }

    const updateUrl = this.updateUrlTemplateValue.replace(":id", encodeURIComponent(id))
    const response = await fetch(updateUrl, {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ plantilla_informe: payload })
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || "No se pudo actualizar la plantilla.")
    }

    return data
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": this.csrfToken()
    }
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.content : ""
  }

  prepareCreateForm() {
    this.editingTemplateId = null
    if (this.hasFormTitleTarget) this.formTitleTarget.textContent = "Nueva plantilla"
    if (this.hasSaveButtonTarget) this.saveButtonTarget.textContent = "Guardar plantilla"
    if (this.hasModalSelectTarget) this.modalSelectTarget.value = ""
    this.resetTemplateForm()
  }

  prepareEditForm(template) {
    this.editingTemplateId = template.id
    if (this.hasFormTitleTarget) this.formTitleTarget.textContent = "Editar plantilla"
    if (this.hasSaveButtonTarget) this.saveButtonTarget.textContent = "Actualizar plantilla"
    if (this.hasModalSelectTarget) this.modalSelectTarget.value = String(template.id)
    if (this.hasNameInputTarget) this.nameInputTarget.value = template.nombre || ""
    this.loadTemplateHtml(template.contenido || "")
  }

  resetTemplateForm() {
    if (this.hasNameInputTarget) this.nameInputTarget.value = ""
    this.loadTemplateHtml("")
  }

  templateContent() {
    return this.hasContentInputTarget ? (this.contentInputTarget.value || "") : ""
  }

  reportContent() {
    const input = this.reportInputElement()
    return input ? (input.value || "") : ""
  }

  loadTemplateHtml(html) {
    if (this.hasContentInputTarget) {
      this.contentInputTarget.value = html
    }

    if (this.hasTemplateEditorTarget && this.templateEditorTarget.editor) {
      this.templateEditorTarget.editor.loadHTML(html || "")
    }
  }

  loadReportHtml(html) {
    const input = this.reportInputElement()
    if (input) {
      input.value = html
    }

    if (this.trixEditorTarget.editor) {
      this.trixEditorTarget.editor.loadHTML(html || "")
    }
  }

  reportInputElement() {
    if (!this.hasTrixEditorTarget) return null

    const inputId = this.trixEditorTarget.getAttribute("input")
    return inputId ? document.getElementById(inputId) : null
  }

  escapeHtml(value) {
    const div = document.createElement("div")
    div.innerText = value
    return div.innerHTML
  }

  openTemplateModal() {
    const modal = this.bootstrapTemplateModal()
    if (!modal) return

    modal.show()
  }

  closeTemplateModal() {
    const modal = this.bootstrapTemplateModal()
    if (!modal) return
    modal.hide()
  }

  bootstrapTemplateModal() {
    if (!this.hasTemplateModalTarget) return null

    if (!this.templateModal) {
      this.templateModal = new bootstrap.Modal(this.templateModalTarget, { backdrop: 'static', keyboard: false })
      this.templateModalTarget.addEventListener("keydown", (event) => {
        if (event.key !== 'Escape') return
        event.stopPropagation()
        this.closeTemplateModal()
      })
      this.templateModalTarget.addEventListener("hidden.bs.modal", () => {
        this.prepareCreateForm()
      })
    }

    return this.templateModal
  }
}
